<template>
  <transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
  >
    <div
        v-if="visible"
        ref="modalRoot"
        tabindex="-1"
        class="fixed inset-0 z-[950] flex items-center justify-center bg-black/70 backdrop-blur-xl p-4"
        @click.self="close"
        @keydown.esc.prevent.stop="close"
        @keydown.enter.prevent.stop="handleEnter"
    >
      <div style="max-width:90%" class="w-full mx-auto max-h-[100vh] overflow-hidden rounded-3xl border border-amber-400/20 bg-slate-950/95 shadow-2xl shadow-black/60 flex flex-col">
        <!-- Header -->
        <div class="px-5 py-4 border-b border-white/10 bg-gradient-to-l from-slate-900 via-slate-900 to-amber-950/30">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <div class="w-9 h-9 rounded-2xl bg-amber-400 text-slate-950 flex items-center justify-center font-black">
                  ↩
                </div>
                <div>
                  <h3 class="text-lg font-black text-slate-50">
                    ثبت مرجوعی فاکتور #{{ order && order.id ? order.id : '---' }}
                  </h3>
                  <p class="text-xs text-slate-400 mt-1">
                    مرجوعی سند جداست؛ آیتم‌های فاکتور اصلی حذف یا کم نمی‌شوند.
                  </p>
                </div>
              </div>
            </div>

            <button
                type="button"
                @click="close"
                class="px-5 py-2.5 rounded-2xl bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs font-bold"
            >
              انصراف
            </button>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
            <div class="rounded-2xl border border-white/10 bg-slate-900/70 p-3">
              <div class="text-[11px] text-slate-500 mb-1">مشتری</div>
              <div class="text-sm font-bold text-slate-100 truncate">{{ customerName }}</div>
              <div class="text-xs text-slate-400 mt-1">{{ customerPhone || '---' }}</div>
            </div>

            <div class="rounded-2xl border border-white/10 bg-slate-900/70 p-3">
              <div class="text-[11px] text-slate-500 mb-1">مبلغ فاکتور</div>
              <div class="text-sm font-black text-slate-100">{{ money(orderAmount) }}</div>
            </div>

            <div class="rounded-2xl border border-rose-400/20 bg-rose-500/10 p-3">
              <div class="text-[11px] text-rose-200/70 mb-1">قبلاً مرجوع شده</div>
              <div class="text-sm font-black text-rose-200">{{ money(orderReturnedAmount) }}</div>
            </div>

            <div class="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-3">
              <div class="text-[11px] text-emerald-200/70 mb-1">خالص بعد از مرجوعی</div>
              <div class="text-sm font-black text-emerald-200">{{ money(orderNetAmount) }}</div>
            </div>

            <div class="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-3">
              <div class="text-[11px] text-amber-200/70 mb-1">مبلغ این مرجوعی</div>
              <div class="text-sm font-black text-amber-200">{{ money(totalReturnAmount) }}</div>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-auto custom-scrollbar">
          <div v-if="loading.items" class="p-10 text-center text-slate-300">
            در حال دریافت آیتم‌های قابل مرجوعی...
          </div>

          <div v-else-if="error" class="p-6">
            <div class="rounded-2xl border border-rose-400/30 bg-rose-500/10 text-rose-200 p-4 text-sm">
              {{ error }}
            </div>
          </div>

          <div v-else class="p-4 space-y-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div class="text-sm text-slate-300">
                <span class="text-amber-300 font-black">{{ returnableCount }}</span>
                ردیف قابل مرجوعی از
                <span class="text-slate-100 font-black">{{ items.length }}</span>
                ردیف
              </div>

              <div class="flex items-center gap-2">
                <button
                    type="button"
                    @click="returnAll"
                    :disabled="!returnableCount"
                    class="px-4 py-2 rounded-xl bg-amber-400 text-slate-950 hover:bg-amber-300 text-xs font-black transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  مرجوعی کل فاکتور
                </button>

                <button
                    type="button"
                    @click="clearAll"
                    class="px-4 py-2 rounded-xl bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs font-bold transition"
                >
                  پاک کردن تعدادها
                </button>
              </div>
            </div>

            <div class="rounded-3xl border border-white/10 bg-slate-900/80 overflow-hidden">
              <table class="w-full text-sm">
                <thead class="sticky top-0 z-10 bg-slate-900 border-b border-white/10 text-slate-300">
                <tr>
                  <th class="px-3 py-3 text-right">کالا</th>
                  <th class="px-3 py-3 text-center">فروخته‌شده</th>
                  <th class="px-3 py-3 text-center">قبلاً مرجوع</th>
                  <th class="px-3 py-3 text-center">قابل مرجوعی</th>
                  <th class="px-3 py-3 text-center">قیمت برگشتی</th>
                  <th class="px-3 py-3 text-center">تعداد مرجوعی</th>
                  <th class="px-3 py-3 text-center">جمع ردیف</th>
                  <th class="px-3 py-3 text-center">عملیات</th>
                </tr>
                </thead>

                <tbody class="divide-y divide-white/5">
                <tr
                    v-for="item in items"
                    :key="item.order_item_id"
                    class="transition-all duration-200"
                    :class="item.returnable_quantity > 0 ? 'hover:bg-slate-800/60' : 'opacity-55 bg-slate-950/30'"
                >
                  <td class="px-3 py-3">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-2xl bg-slate-800 border border-white/10 flex items-center justify-center text-slate-500">
                        کالا
                      </div>
                      <div class="min-w-0">
                        <div class="text-slate-50 font-bold truncate max-w-[260px]">
                          {{ item.product_name || '---' }}
                        </div>
                        <div class="text-[11px] text-slate-500 mt-1">
                          آیتم فروش: {{ item.order_item_id }} / کالا: {{ item.product_id || '---' }}
                        </div>
                        <div
                            v-if="Number(item.returnable_quantity || 0) <= 0"
                            class="text-[11px] text-rose-300 mt-1"
                        >
                          این ردیف قبلاً کامل مرجوع شده است
                        </div>
                      </div>
                    </div>
                  </td>

                  <td class="px-3 py-3 text-center text-slate-200 font-bold">
                    {{ item.sold_quantity }}
                  </td>

                  <td class="px-3 py-3 text-center">
                    <span class="px-2 py-1 rounded-lg bg-rose-500/10 text-rose-200 border border-rose-400/20 text-xs font-bold">
                      {{ item.already_returned_quantity || 0 }}
                    </span>
                  </td>

                  <td class="px-3 py-3 text-center">
                    <span class="px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-200 border border-emerald-400/20 text-xs font-bold">
                      {{ item.returnable_quantity || 0 }}
                    </span>
                  </td>

                  <td class="px-3 py-3 text-center text-slate-100 font-bold">
                    <div class="flex flex-col items-center gap-1">
  <span
      v-if="regularReturnPrice(item) && regularReturnPrice(item) > unitReturnPrice(item)"
      class="text-[11px] text-slate-500 line-through"
  >
    {{ money(regularReturnPrice(item)) }}
  </span>

                      <span class="text-slate-100 font-black">
    {{ money(unitReturnPrice(item)) }}
  </span>

                      <span
                          v-if="returnDiscountPercent(item)"
                          class="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-200 border border-emerald-400/20 text-[10px] font-black"
                      >
    {{ returnDiscountPercent(item) }}٪ تخفیف
  </span>
                    </div>                  </td>

                  <td class="px-3 py-3 text-center">
                    <div class="inline-flex items-center rounded-2xl border border-amber-400/30 bg-slate-950 overflow-hidden">
                      <button
                          type="button"
                          @click="changeQuantity(item, Number(item.return_quantity || 0) - 1)"
                          :disabled="!canReturn(item)"
                          class="px-2 py-1.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
                      >
                        -
                      </button>

                      <input
                          type="number"
                          min="0"
                          :max="item.returnable_quantity"
                          v-model.number="item.return_quantity"
                          @change="normalizeQuantity(item)"
                          @keydown.enter.prevent.stop="normalizeQuantity(item)"
                          @keydown.esc.prevent.stop="focusModal"
                          :disabled="!canReturn(item)"
                          class="w-16 text-center bg-slate-950 border-x border-white/10 text-slate-50 font-black focus:outline-none disabled:opacity-50"
                      />

                      <button
                          type="button"
                          @click="changeQuantity(item, Number(item.return_quantity || 0) + 1)"
                          :disabled="!canReturn(item)"
                          class="px-2 py-1.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
                      >
                        +
                      </button>
                    </div>
                  </td>

                  <td class="px-3 py-3 text-center">
                    <div class="text-amber-300 font-black">
                      {{ money(lineTotal(item)) }}
                    </div>
                  </td>

                  <td class="px-3 py-3 text-center">
                    <div class="flex items-center justify-center gap-2">
                      <button
                          type="button"
                          @click="returnLine(item)"
                          :disabled="!canReturn(item)"
                          class="px-3 py-1.5 rounded-xl bg-amber-500/15 text-amber-200 border border-amber-400/30 hover:bg-amber-500/25 text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        کل ردیف
                      </button>
                      <button
                          type="button"
                          @click="changeQuantity(item, 0)"
                          class="px-3 py-1.5 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 text-xs"
                      >
                        حذف از مرجوعی
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="!items.length">
                  <td colspan="8" class="px-6 py-12 text-center text-slate-500">
                    آیتمی برای مرجوعی پیدا نشد.
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div class="lg:col-span-2 rounded-3xl border border-white/10 bg-slate-900/80 p-4">
                <label class="block text-xs font-bold text-slate-300 mb-2">
                  توضیحات مرجوعی
                </label>
                <textarea
                    @keydown.enter.stop
                    v-model="note"
                    rows="3"
                    class="w-full rounded-2xl border border-white/10 bg-slate-950/80 text-slate-100 placeholder-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-amber-400/50 focus:ring-2 focus:ring-amber-400/10"
                    placeholder="مثلاً: مرجوعی به درخواست مشتری..."
                ></textarea>
              </div>

              <div class="rounded-3xl border border-amber-400/20 bg-amber-500/10 p-4">
                <div class="flex justify-between text-sm mb-2">
                  <span class="text-slate-300">تعداد ردیف انتخاب‌شده</span>
                  <span class="text-slate-50 font-black">{{ selectedReturnRows.length }}</span>
                </div>
                <div class="flex justify-between text-sm mb-4">
                  <span class="text-slate-300">جمع مبلغ مرجوعی</span>
                  <span
                      v-if="selectedReturnRows.length"
                      class="mr-2 inline-flex items-center rounded-xl border px-2 py-1 text-[10px] font-bold"
                      :class="isFullDocumentReturnSelected
      ? 'border-pink-400/30 bg-pink-500/10 text-pink-200'
      : 'border-orange-400/25 bg-orange-500/10 text-orange-200'"
                  >
  {{ isFullDocumentReturnSelected ? 'مرجوعی کامل سندی' : 'مرجوعی جزئی سندی' }}
</span>                </div>

                <div
                    v-if="submitError"
                    class="mb-3 rounded-xl border border-rose-400/30 bg-rose-500/10 text-rose-200 text-xs p-3 leading-6"
                >
                  {{ submitError }}
                </div>

                <button
                    type="button"
                    @click="submitReturn"
                    :disabled="loading.submit || !selectedReturnRows.length"
                    class='flex-1 px-6 py-3.5 rounded-2xl text-sm font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 text-slate-950 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40 w-full'
                >
                  {{ loading.submit ? 'در حال ثبت مرجوعی...' : 'ثبت سند مرجوعی' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'PosReturnCreateModal',

  props: {
    apiGetPos: {
      type: Function,
      required: true
    },
    apiPostPos: {
      type: Function,
      required: true
    },
    formatCurrency: {
      type: Function,
      default: null
    },
    toast: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      visible: false,
      order: null,
      items: [],
      note: '',
      error: '',
      submitError: '',
      loading: {
        items: false,
        submit: false
      }
    }
  },

  computed: {
    customerName() {
      const o = this.order || {}
      return (
          o.customer_display_name ||
          o.customer_name ||
          [o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ') ||
          'مشتری حضوری'
      )
    },

    customerPhone() {
      const o = this.order || {}
      return o.phone_number || o.customer_phone || o.customer_phone_number || ''
    },

    orderAmount() {
      const o = this.order || {}
      return Number(o.final_amount || o.total_price || 0)
    },

    orderReturnedAmount() {
      const o = this.order || {}
      return Number(o.total_returned_amount || 0)
    },

    orderNetAmount() {
      const o = this.order || {}
      const explicit = o.net_amount_after_returns
      if (explicit !== undefined && explicit !== null && explicit !== '') return Number(explicit)
      return Math.max(this.orderAmount - this.orderReturnedAmount, 0)
    },

    returnableCount() {
      return this.items.filter(i => Number(i.returnable_quantity || 0) > 0).length
    },

    selectedReturnRows() {
      return this.items.filter(i => Number(i.return_quantity || 0) > 0)
    },

    totalReturnAmount() {
      return this.selectedReturnRows.reduce((sum, item) => sum + this.lineTotal(item), 0)
    },
    isFullDocumentReturnSelected() {
      const returnableItems = this.items.filter(i => Number(i.returnable_quantity || 0) > 0)

      if (!returnableItems.length) return false

      return returnableItems.every(item => {
        const selected = Number(item.return_quantity || 0)
        const max = Number(item.returnable_quantity || 0)

        return max > 0 && selected === max
      })
    },
  },

  methods: {
    unitReturnPrice(item) {
      return Number(
          item.unit_return_price ||
          item.unit_price ||
          item.final_unit_price ||
          item.price ||
          0
      )
    },

    regularReturnPrice(item) {
      return Number(
          item.regular_price ||
          item.consumer_price ||
          item.price_before_discount ||
          item.product_regular_price ||
          0
      )
    },

    returnDiscountPercent(item) {
      const regular = this.regularReturnPrice(item)
      const unit = this.unitReturnPrice(item)

      if (!regular || !unit || unit >= regular) return 0

      return Math.round(((regular - unit) / regular) * 100)
    },
    focusModal() {
      if (this.$refs.modalRoot && this.$refs.modalRoot.focus) {
        this.$refs.modalRoot.focus()
      }
    },

    handleEnter(e) {
      const tag = e && e.target && e.target.tagName
          ? e.target.tagName.toUpperCase()
          : ''

      if (tag === 'TEXTAREA') return

      if (tag === 'INPUT' || tag === 'SELECT') {
        return
      }

      if (this.loading.items || this.loading.submit) return

      this.submitReturn()
    },
    async openForOrder(order) {
      if (!order || !order.id) {
        this.toast && this.toast('فاکتور برای مرجوعی پیدا نشد', 'error')
        return
      }

      if (order.status && order.status !== 'cf') {
        this.toast && this.toast('فقط فاکتور نهایی‌شده قابل مرجوعی است', 'error')
        return
      }

      this.visible = true
      this.order = order
      this.items = []
      this.note = ''
      this.error = ''
      this.submitError = ''

      this.$nextTick(() => {
        this.focusModal()
      })

      await this.fetchReturnableItems(order.id)

      this.$nextTick(() => {
        this.focusModal()
      })
    },
    close(force = false) {
      if (this.loading.submit && !force) return

      this.visible = false
      this.order = null
      this.items = []
      this.note = ''
      this.error = ''
      this.submitError = ''
    },

    async fetchReturnableItems(orderId) {
      this.loading.items = true
      this.error = ''

      try {
        const data = await this.apiGetPos('order-returns/returnable-items/', {
          order_id: orderId
        })

        const rows = Array.isArray(data)
            ? data
            : Array.isArray(data && data.results)
                ? data.results
                : []

        this.items = rows.map(row => ({
          ...row,
          sold_quantity: Number(row.sold_quantity || 0),
          already_returned_quantity: Number(row.already_returned_quantity || 0),
          returnable_quantity: Number(row.returnable_quantity || 0),
          unit_return_price: Number(row.unit_return_price || 0),
          return_quantity: 0
        }))
      } catch (e) {
        this.error = this.extractError(e) || 'دریافت آیتم‌های قابل مرجوعی انجام نشد'
      } finally {
        this.loading.items = false
      }
    },

    canReturn(item) {
      return Number(item && item.returnable_quantity || 0) > 0
    },

    normalizeQuantity(item) {
      const max = Number(item.returnable_quantity || 0)
      let qty = Number(item.return_quantity || 0)

      if (Number.isNaN(qty) || qty < 0) qty = 0
      if (qty > max) qty = max

      this.$set(item, 'return_quantity', qty)
    },

    changeQuantity(item, qty) {
      this.$set(item, 'return_quantity', qty)
      this.normalizeQuantity(item)
    },

    returnLine(item) {
      this.changeQuantity(item, Number(item.returnable_quantity || 0))
    },

    returnAll() {
      this.items.forEach(item => {
        this.changeQuantity(item, Number(item.returnable_quantity || 0))
      })
    },

    clearAll() {
      this.items.forEach(item => {
        this.changeQuantity(item, 0)
      })
    },

    lineTotal(item) {
      return Number(item.return_quantity || 0) * this.unitReturnPrice(item)
    },

    async submitReturn() {
      this.submitError = ''

      const selectedItems = this.selectedReturnRows

      if (!selectedItems.length) {
        this.submitError = 'حداقل یک آیتم برای مرجوعی انتخاب کنید'
        return
      }

      for (const item of selectedItems) {
        const qty = Number(item.return_quantity || 0)
        const max = Number(item.returnable_quantity || 0)

        if (qty <= 0 || qty > max) {
          this.submitError = `تعداد مرجوعی ${item.product_name || ''} معتبر نیست`
          return
        }
      }

      const payload = {
        order_id: this.order.id,
        note: this.note || null,
        items: selectedItems.map(item => ({
          order_item_id: item.order_item_id,
          quantity: Number(item.return_quantity)
        }))
      }

      this.loading.submit = true

      try {
        const created = await this.apiPostPos('order-returns/', payload)

        this.toast && this.toast(
            this.isFullDocumentReturnSelected
                ? 'مرجوعی کامل سندی با موفقیت ثبت شد'
                : 'مرجوعی جزئی سندی با موفقیت ثبت شد',
            'success'
        )

        const currentOrder = this.order

        this.$emit('return-created', {
          returnDoc: created,
          order: currentOrder
        })

        this.loading.submit = false
        this.close(true)
      } catch (e) {
        this.submitError = this.extractError(e) || 'ثبت مرجوعی انجام نشد'
      } finally {
        this.loading.submit = false
      }
    },

    money(value) {
      if (this.formatCurrency) return this.formatCurrency(value)
      const num = Number(value || 0)
      if (Number.isNaN(num)) return value || '0'
      return num.toLocaleString('fa-IR') + ' تومان'
    },

    extractError(e) {
      const data = e && e.response && e.response.data ? e.response.data : e
      if (!data) return ''
      if (typeof data === 'string') return data
      if (data.detail) return data.detail
      if (data.error) return data.error
      if (data.items) return Array.isArray(data.items) ? data.items.join('، ') : String(data.items)
      try {
        return Object.values(data).flat().join('، ')
      } catch (_) {
        return ''
      }
    }
  }
}
</script>
